import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  getReadings,
  getEmployees,
  updateReading,
  deleteReading,
  analyzeMeterReading,
} from "../services/api";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import {
  Table2,
  Download,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LIMIT_OPTIONS = [10, 30, 50, 100, 200];

const ROLE_LABEL = {
  lector: "Auditor",
  "lector-plus": "Auditor Plus",
  field_agent: "Auditor Plus",
  admin: "Admin",
  "admin-plus": "Admin Plus",
};

const dotIcon = L.divIcon({
  className: "",
  html: '<div style="width:10px;height:10px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

const incidentDotIcon = L.divIcon({
  className: "",
  html: '<div style="width:12px;height:12px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const batchDotIcon = L.divIcon({
  className: "",
  html: '<div style="width:12px;height:12px;border-radius:50%;background:linear-gradient(to right,#2563eb 50%,#93c5fd 50%);border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});


export default function ReadingsPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const [readings, setReadings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [editData, setEditData] = useState({ meter_code: "", reading_value: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportView, setShowExportView] = useState(false);
  const [activeExportPanel, setActiveExportPanel] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportEmpSearch, setExportEmpSearch] = useState("");
  const [exportConfig, setExportConfig] = useState({
    userId: "",
    userLabel: "Todos",
    roles: [],
    from: "",
    to: "",
    allRecords: false,
    limit: 500,
    columns: {
      user_name: true,
      user_email: true,
      user_role: true,
      meter_code: true,
      created_at: true,
      latitude: true,
      longitude: true,
      maps_link: true,
      notes: true,
      image_url: false,
      facade_url: false,
    },
  });
  const [lightbox, setLightbox] = useState(null);
  const [filters, setFilters] = useState({
    userId: "",
    from: "",
    to: "",
    meterCode: "",
  });
  const [limit, setLimit] = useState(30);
  const employeeRoleMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e.role])),
    [employees],
  );
  useEffect(() => {
    getEmployees()
      .then(setEmployees)
      .catch(() => {});
  }, []);

  const load = useCallback(
    async (f = filters, p = 1, l = limit) => {
      setLoading(true);
      try {
        const data = await getReadings({ ...f, page: p, limit: l });
        setReadings(data.data);
        setTotal(data.pagination?.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [filters, limit],
  );

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (selected)
      setEditData({
        meter_code: selected.meter_code || "",
        reading_value: selected.reading_value || "",
        notes: selected.notes || "",
      });
  }, [selected]);

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await updateReading(selected.id, editData);
      setReadings((prev) =>
        prev.map((r) => (r.id === selected.id ? { ...r, ...updated } : r)),
      );
      setSelected(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function runAnalyze() {
    setAnalyzing(true);
    try {
      const result = await analyzeMeterReading(selected.image_url, selected.id);
      setEditData((d) => ({
        ...d,
        meter_code: result.numero_medidor || d.meter_code,
        reading_value: (result.lectura_m3 && result.lectura_m3 !== "REVISAR") ? result.lectura_m3 : d.reading_value,
      }));
    } catch {
      /* mantener datos actuales */
    } finally {
      setAnalyzing(false);
    }
  }

  function handleDelete() {
    setShowDeleteModal(true);
  }

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    load(filters, 1);
  }

  function clearFilters() {
    const empty = { userId: "", from: "", to: "", meterCode: "" };
    setFilters(empty);
    setPage(1);
    load(empty, 1);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const data = await getReadings({
        userId: exportConfig.userId,
        from: exportConfig.from,
        to: exportConfig.to,
        page: 1,
        limit: exportConfig.allRecords ? 99999 : exportConfig.limit,
      });
      const cols = exportConfig.columns;
      let rows = data.data;
      if (exportConfig.roles.length > 0)
        rows = rows.filter((r) =>
          exportConfig.roles.includes(employeeRoleMap[r.user_id]),
        );
      const exportRows = rows.map((r) => {
        const row = {};
        if (cols.user_name) row["Usuario"] = r.user_name;
        if (cols.user_email) row["Email"] = r.user_email;
        if (cols.user_role) row["Rol"] = employeeRoleMap[r.user_id] || "";
        if (cols.meter_code) row["Código medidor"] = r.meter_code || "";
        if (cols.created_at)
          row["Fecha"] = format(new Date(r.created_at), "dd/MM/yyyy HH:mm", {
            locale: es,
          });
        if (cols.latitude) row["Latitud"] = r.latitude;
        if (cols.longitude) row["Longitud"] = r.longitude;
        if (cols.maps_link)
          row["Link Maps"] =
            `https://www.google.com/maps?q=${r.latitude},${r.longitude}`;
        if (cols.notes) row["Observaciones"] = r.notes || "";
        if (cols.image_url) row["Foto lectura"] = r.image_url;
        if (cols.facade_url) row["Foto fachada"] = r.facade_url || "";
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Medidores");
      XLSX.writeFile(
        wb,
        `medidores_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`,
      );
      setShowExportView(false);
    } catch (err) {
      alert("Error al exportar: " + err.message);
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <DeleteConfirmModal
        visible={showDeleteModal}
        message="¿Eliminar esta lectura? Esta acción no se puede deshacer."
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await deleteReading(selected.id);
          setReadings((prev) => prev.filter((r) => r.id !== selected.id));
          setTotal((t) => t - 1);
          setShowDeleteModal(false);
          setSelected(null);
        }}
      />
      {/* Vista de exportación */}
      {showExportView && (
        <div style={styles.exportPage}>
          {/* Header */}
          <div style={styles.exportPageHeader}>
            <button
              style={styles.exportBackBtn}
              onClick={() => setShowExportView(false)}
            >
              <ChevronLeft size={18} /> Volver
            </button>
            <h2 style={styles.exportPageTitle}>Exportar Excel</h2>
            <button
              style={styles.exportDownloadBtn}
              onClick={handleExport}
              disabled={exporting}
            >
              <Download size={15} />
              {exporting ? "Exportando..." : "Descargar Excel"}
            </button>
          </div>

          {/* Resumen de filtros activos */}
          <div style={styles.exportSummary}>
            {exportConfig.userLabel !== "Todos" && (
              <span style={styles.exportTag}>👤 {exportConfig.userLabel}</span>
            )}
            {exportConfig.roles.length > 0 && (
              <span style={styles.exportTag}>
                🏷 {exportConfig.roles.join(", ")}
              </span>
            )}
            {exportConfig.from && (
              <span style={styles.exportTag}>📅 Desde {exportConfig.from}</span>
            )}
            {exportConfig.to && (
              <span style={styles.exportTag}>📅 Hasta {exportConfig.to}</span>
            )}
            {exportConfig.allRecords ? (
              <span style={styles.exportTag}>📋 Todos los medidores</span>
            ) : (
              <span style={styles.exportTag}>
                📋 {exportConfig.limit} registros
              </span>
            )}
            <span style={styles.exportTag}>
              🗂 {Object.values(exportConfig.columns).filter(Boolean).length}{" "}
              columnas
            </span>
          </div>

          {/* Botones de filtro */}
          <div style={styles.exportFilterRow}>
            {[
              {
                key: "collaborator",
                label: "Usuario",
                desc: exportConfig.userLabel,
              },
              {
                key: "role",
                label: "Rol",
                desc:
                  exportConfig.roles.length === 0
                    ? "Todos"
                    : exportConfig.roles.join(", "),
              },
              {
                key: "dates",
                label: "Fechas",
                desc:
                  exportConfig.from || exportConfig.to
                    ? `${exportConfig.from || "…"} → ${exportConfig.to || "…"}`
                    : "Todas",
              },
              {
                key: "columns",
                label: "Columnas",
                desc: `${Object.values(exportConfig.columns).filter(Boolean).length} seleccionadas`,
              },
              {
                key: "quantity",
                label: "Cantidad",
                desc: exportConfig.allRecords
                  ? "Todas"
                  : `${exportConfig.limit} registros`,
              },
            ].map(({ key, label, desc }) => (
              <button
                key={key}
                style={{
                  ...styles.exportFilterBtn,
                  ...(activeExportPanel === key
                    ? styles.exportFilterBtnActive
                    : {}),
                }}
                onClick={() =>
                  setActiveExportPanel(activeExportPanel === key ? null : key)
                }
              >
                <span style={styles.exportFilterBtnLabel}>{label}</span>
                <span style={styles.exportFilterBtnDesc}>{desc}</span>
              </button>
            ))}
          </div>

          {/* Paneles */}
          {activeExportPanel && (
            <div
              style={styles.exportPanelOverlay}
              onClick={() => setActiveExportPanel(null)}
            >
              <div
                style={styles.exportPanel}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Panel: Colaborador */}
                {activeExportPanel === "collaborator" && (
                  <>
                    <p style={styles.exportPanelTitle}>Usuario</p>
                    <div style={styles.exportEmpWrap}>
                      <div style={styles.exportEmpSearch}>
                        <Search size={13} color="#9a9a93" />
                        <input
                          style={styles.exportEmpInput}
                          type="text"
                          placeholder="Buscar..."
                          value={exportEmpSearch}
                          onChange={(e) => setExportEmpSearch(e.target.value)}
                          autoFocus
                        />
                        {exportEmpSearch && (
                          <button
                            style={styles.exportEmpClear}
                            onClick={() => setExportEmpSearch("")}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <div style={styles.exportEmpList}>
                        {[
                          { id: "", name: "Todos los usuarios", email: "" },
                          ...employees,
                        ]
                          .filter(
                            (e) =>
                              !exportEmpSearch ||
                              e.name
                                .toLowerCase()
                                .includes(exportEmpSearch.toLowerCase()) ||
                              e.email
                                .toLowerCase()
                                .includes(exportEmpSearch.toLowerCase()),
                          )
                          .map((e) => {
                            const active = exportConfig.userId === e.id;
                            return (
                              <div
                                key={e.id || "all"}
                                style={{
                                  ...styles.exportEmpItem,
                                  ...(active ? styles.exportEmpItemActive : {}),
                                }}
                                onClick={() => {
                                  setExportConfig((c) => ({
                                    ...c,
                                    userId: e.id,
                                    userLabel: e.name,
                                  }));
                                  setActiveExportPanel(null);
                                }}
                              >
                                <span style={styles.exportEmpDot(active)} />
                                <div>
                                  <div
                                    style={{                                  fontSize: 13,
                                      fontWeight: active ? 600 : 400,
                                      color: active ? "#D4620D" : "#1a1a18",
                                    }}
                                  >
                                    {e.name}
                                  </div>
                                  {e.email && (
                                    <div
                                      style={{ fontSize: 13, color: "var(--text-2)" }}
                                    >
                                      {e.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </>
                )}

                {/* Panel: Rol */}
                {activeExportPanel === "role" && (
                  <>
                    <p style={styles.exportPanelTitle}>Rol</p>
                    <p
                      style={{                    fontSize: 13,
                        color: "var(--text-2)",
                        marginBottom: 14,
                        marginTop: -8,
                      }}
                    >
                      Seleccioná uno o varios roles. Sin selección = todos.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {[
                        { key: "lector", label: "Auditor" },
                        { key: "lector-plus", label: "Auditor Plus" },
                        { key: "field_agent", label: "Field Agent" },
                      ].map(({ key, label }) => {
                        const on = exportConfig.roles.includes(key);
                        return (
                          <div
                            key={key}
                            style={styles.exportColRow}
                            onClick={() =>
                              setExportConfig((c) => ({
                                ...c,
                                roles: on
                                  ? c.roles.filter((r) => r !== key)
                                  : [...c.roles, key],
                              }))
                            }
                          >
                            <div
                              style={{
                                ...styles.exportRoleToggleBox,
                                ...(on ? styles.exportRoleToggleBoxOn : {}),
                              }}
                            >
                              {on && (
                                <span
                                  style={{
                                    color: "#fff",                                fontSize: 13,
                                    fontWeight: 700,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 13, color: "var(--text)" }}>
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      style={{ ...styles.exportPanelApply, marginTop: 16 }}
                      onClick={() => setActiveExportPanel(null)}
                    >
                      Aplicar
                    </button>
                  </>
                )}

                {/* Panel: Fechas */}
                {activeExportPanel === "dates" && (
                  <>
                    <p style={styles.exportPanelTitle}>Rango de fechas</p>
                    <label style={styles.exportPanelLabel}>Desde</label>
                    <input
                      style={{ ...styles.fieldInput, marginBottom: 12 }}
                      type="date"
                      value={exportConfig.from}
                      onChange={(e) =>
                        setExportConfig((c) => ({ ...c, from: e.target.value }))
                      }
                    />
                    <label style={styles.exportPanelLabel}>Hasta</label>
                    <input
                      style={styles.fieldInput}
                      type="date"
                      value={exportConfig.to}
                      onChange={(e) =>
                        setExportConfig((c) => ({ ...c, to: e.target.value }))
                      }
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                      <button
                        style={styles.exportPanelSecondary}
                        onClick={() =>
                          setExportConfig((c) => ({ ...c, from: "", to: "" }))
                        }
                      >
                        Limpiar
                      </button>
                      <button
                        style={styles.exportPanelApply}
                        onClick={() => setActiveExportPanel(null)}
                      >
                        Aplicar
                      </button>
                    </div>
                  </>
                )}

                {/* Panel: Columnas */}
                {activeExportPanel === "columns" && (
                  <>
                    <p style={styles.exportPanelTitle}>Columnas a exportar</p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {[
                        { key: "user_name", label: "Usuario" },
                        { key: "user_email", label: "Email" },
                        { key: "user_role", label: "Rol" },
                        { key: "meter_code", label: "Código medidor" },
                        { key: "created_at", label: "Fecha" },
                        { key: "latitude", label: "Latitud" },
                        { key: "longitude", label: "Longitud" },
                        { key: "maps_link", label: "Link Google Maps" },
                        { key: "notes", label: "Observaciones" },
                        { key: "image_url", label: "Foto lectura (URL)" },
                        { key: "facade_url", label: "Foto fachada (URL)" },
                      ].map(({ key, label }) => {
                        const on = exportConfig.columns[key];
                        return (
                          <div
                            key={key}
                            style={styles.exportColRow}
                            onClick={() =>
                              setExportConfig((c) => ({
                                ...c,
                                columns: { ...c.columns, [key]: !on },
                              }))
                            }
                          >
                            <div
                              style={{
                                ...styles.exportRoleToggleBox,
                                ...(on ? styles.exportRoleToggleBoxOn : {}),
                              }}
                            >
                              {on && (
                                <span
                                  style={{
                                    color: "#fff",                                fontSize: 13,
                                    fontWeight: 700,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 13, color: "var(--text)" }}>
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      style={{ ...styles.exportPanelApply, marginTop: 16 }}
                      onClick={() => setActiveExportPanel(null)}
                    >
                      Aplicar
                    </button>
                  </>
                )}

                {/* Panel: Cantidad */}
                {activeExportPanel === "quantity" && (
                  <>
                    <p style={styles.exportPanelTitle}>Cantidad de registros</p>
                    <div
                      style={styles.exportColRow}
                      onClick={() =>
                        setExportConfig((c) => ({
                          ...c,
                          allRecords: !c.allRecords,
                        }))
                      }
                    >
                      <div
                        style={{
                          ...styles.exportRoleToggleBox,
                          ...(exportConfig.allRecords
                            ? styles.exportRoleToggleBoxOn
                            : {}),
                        }}
                      >
                        {exportConfig.allRecords && (
                          <span
                            style={{
                              color: "#fff",                          fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: 13, color: "var(--text)" }}>
                        Exportar todos los medidores
                      </span>
                    </div>
                    {!exportConfig.allRecords && (
                      <>
                        <label
                          style={{ ...styles.exportPanelLabel, marginTop: 14 }}
                        >
                          Cantidad exacta
                        </label>
                        <input
                          style={styles.fieldInput}
                          type="number"
                          min={1}
                          value={exportConfig.limit}
                          onChange={(e) =>
                            setExportConfig((c) => ({
                              ...c,
                              limit: parseInt(e.target.value) || 1,
                            }))
                          }
                        />
                      </>
                    )}
                    <button
                      style={{ ...styles.exportPanelApply, marginTop: 16 }}
                      onClick={() => setActiveExportPanel(null)}
                    >
                      Aplicar
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Encabezado */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          Medidores <span style={styles.badge}>{total}</span>
        </h1>
        <div style={styles.headerActions}>
          {isAdminPlus && (
            <button
              style={styles.btnExport}
              onClick={() => setShowExportView(true)}
            >
              <Download size={14} /> Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <form
        onSubmit={applyFilters}
        style={styles.filters}
      >
        <select
          style={styles.filterInput}
          value={filters.userId}
          onChange={(e) =>
            setFilters((f) => ({ ...f, userId: e.target.value }))
          }
        >
          <option value="">Todos los Usuarios</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        <input
          style={styles.filterInput}
          type="text"
          placeholder="Medidor"
          value={filters.meterCode}
          onChange={(e) =>
            setFilters((f) => ({ ...f, meterCode: e.target.value }))
          }
        />
        <input
          style={styles.filterInput}
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
        />
        <input
          style={styles.filterInput}
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
        />
        <input
          style={{ ...styles.filterInput, width: 120 }}
          type="number"
          min={1}
          max={99999}
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value) || 1)}
          onBlur={(e) => {
            const l = Math.max(1, parseInt(e.target.value) || 1);
            setLimit(l);
            setPage(1);
            load(filters, 1, l);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const l = Math.max(1, parseInt(e.target.value) || 1);
              setLimit(l);
              setPage(1);
              load(filters, 1, l);
            }
          }}
          placeholder="Por página"
        />
        <button style={styles.btnPrimary} type="submit">
          <Filter size={13} /> Filtrar
        </button>
        <button
          style={styles.btnSecondary}
          type="button"
          onClick={clearFilters}
        >
          <X size={13} /> Limpiar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Cargando...</div>
      ) : (
        <>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {[
                    "Rol",
                    "Usuario",
                    "Código",
                    "Medición",
                    "Fecha",
                    ...(isAdminPlus ? ["Ubicación"] : []),
                    "Obs.",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {readings.map((r) => (
                  <tr
                    key={r.id}
                    style={styles.tr}
                    onClick={() => setSelected(r)}
                  >
                    <td style={styles.td}>
                      <span style={styles.roleBadge}>
                        {ROLE_LABEL[employeeRoleMap[r.user_id]] || employeeRoleMap[r.user_id] || "—"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.userName}>{r.user_name}</div>
                      <div style={styles.userEmail}>{r.user_email}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>
                        {r.meter_code || "—"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 700, color: "var(--text-2)", fontSize: 13 }}>
                        {r.reading_value || "—"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {format(new Date(r.created_at), "dd/MM/yy HH:mm", {
                        locale: es,
                      })}
                    </td>
                    {isAdminPlus && (
                      <td style={styles.td}>
                        <a
                          href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.mapPinLink}
                          onClick={(e) => e.stopPropagation()}
                          title={`${parseFloat(r.latitude).toFixed(5)}, ${parseFloat(r.longitude).toFixed(5)}`}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                              fill="#EA4335"
                            />
                            <circle cx="12" cy="9" r="2.8" fill="white" />
                          </svg>
                        </a>
                      </td>
                    )}
                    <td style={styles.td}>
                      <span style={styles.notes}>{r.notes || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={page === 1}
              onClick={() => {
                const p = page - 1;
                setPage(p);
                load(filters, p, limit);
              }}
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span style={styles.pageInfo}>
              Página {page} de {totalPages} · {total} registros
            </span>
            <button
              style={styles.pageBtn}
              disabled={page >= totalPages}
              onClick={() => {
                const p = page + 1;
                setPage(p);
                load(filters, p, limit);
              }}
            >
              Siguiente <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}

      {/* Modal detalle / edición de lectura */}
      {selected && (
        <div style={styles.overlay} onClick={() => setSelected(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Editar medidor</h2>
              <button style={styles.closeBtn} onClick={() => setSelected(null)}>
                <X size={18} />
              </button>
            </div>

            {/* Images — solo admin-plus */}
            {isAdminPlus && (
              <div style={styles.modalImages}>
                <div style={{ flex: 1 }}>
                  <p style={styles.imgLabel}>Medidor</p>
                  <div
                    style={styles.imgBox}
                    onClick={() => setLightbox(cloudinaryPreview(selected.image_url, 1400))}
                  >
                    <img
                      src={cloudinaryPreview(selected.image_url, 800)}
                      alt=""
                      style={{ ...styles.modalImg, cursor: "zoom-in" }}
                    />
                  </div>
                </div>
                {selected.facade_url && (
                  <div style={{ flex: 1 }}>
                    <p style={styles.imgLabel}>Fachada</p>
                    <div
                      style={styles.imgBox}
                      onClick={() => setLightbox(cloudinaryPreview(selected.facade_url, 1400))}
                    >
                      <img
                        src={cloudinaryPreview(selected.facade_url, 800)}
                        alt=""
                        style={{ ...styles.modalImg, cursor: "zoom-in" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Form + read-only fields */}
            <div style={styles.modalBody}>
              {/* Read-only */}
              <div style={styles.modalInfo}>
                <Row label="Usuario" value={selected.user_name} />
                <Row label="Email" value={selected.user_email} />
                <Row
                  label="Fecha"
                  value={format(
                    new Date(selected.created_at),
                    "dd/MM/yyyy HH:mm",
                    { locale: es },
                  )}
                />
                {isAdminPlus && (
                  <>
                    <Row
                      label="Latitud"
                      value={
                        <a
                          href={`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.coordsLink}
                        >
                          {parseFloat(selected.latitude).toFixed(6)}
                        </a>
                      }
                    />
                    <Row
                      label="Longitud"
                      value={
                        <a
                          href={`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.coordsLink}
                        >
                          {parseFloat(selected.longitude).toFixed(6)}
                        </a>
                      }
                    />
                  </>
                )}
              </div>

              {/* Editable */}
              <div style={styles.modalForm}>
                {isAdminPlus ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <label style={{ ...styles.fieldLabel, margin: 0 }}>Código de medidor</label>
                      <button
                        style={{ ...styles.btnAnalyze, opacity: analyzing || !selected?.image_url ? 0.6 : 1 }}
                        disabled={analyzing || !selected?.image_url}
                        onClick={runAnalyze}
                      >
                        {analyzing ? "Analizando…" : "✨ Analizar IA"}
                      </button>
                    </div>
                    <input
                      style={styles.fieldInput}
                      value={editData.meter_code}
                      onChange={(e) => setEditData((d) => ({ ...d, meter_code: e.target.value }))}
                      placeholder="Sin código"
                    />
                    <label style={{ ...styles.fieldLabel, marginTop: 14 }}>
                      Valor de medición
                    </label>
                    <input
                      style={styles.fieldInput}
                      value={editData.reading_value}
                      onChange={(e) => setEditData((d) => ({ ...d, reading_value: e.target.value }))}
                      placeholder="Sin medición"
                    />
                    <label style={{ ...styles.fieldLabel, marginTop: 14 }}>
                      Observaciones
                    </label>
                    <textarea
                      style={{ ...styles.fieldInput, minHeight: 70, resize: "vertical" }}
                      value={editData.notes}
                      onChange={(e) => setEditData((d) => ({ ...d, notes: e.target.value }))}
                      placeholder="Sin observaciones"
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 18,
                        justifyContent: "space-between",
                      }}
                    >
                      <button style={styles.btnDelete} onClick={handleDelete}>
                        Eliminar lectura
                      </button>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          style={styles.btnCancel}
                          onClick={() => setSelected(null)}
                        >
                          Cancelar
                        </button>
                        <button
                          style={styles.btnSave}
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "Guardando..." : "Guardar cambios"}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    {selected?.meter_code && (
                      <>
                        <label style={styles.fieldLabel}>
                          Código de medidor
                        </label>
                        <p
                          style={{                        fontSize: 14,
                            color: "var(--text)",
                            fontWeight: 700,
                            margin: "0 0 16px",
                          }}
                        >
                          {selected.meter_code}
                        </p>
                      </>
                    )}
                    {selected?.notes && (
                      <>
                        <label style={styles.fieldLabel}>Observaciones</label>
                        <p
                          style={{ fontSize: 13, color: "var(--text-2)", margin: 0 }}
                        >
                          {selected.notes}
                        </p>
                      </>
                    )}
                    <div
                      style={{
                        marginTop: 18,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        style={styles.btnCancel}
                        onClick={() => setSelected(null)}
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Lightbox */}
      {lightbox && (
        <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
          <button
            style={styles.lightboxClose}
            onClick={() => setLightbox(null)}
          >
            <X size={22} />
          </button>
          <img
            src={lightbox}
            alt=""
            style={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <span style={{ fontSize: 13, color: "var(--text-2)", fontWeight: 700 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 700 }}>
        {value}
      </span>
    </div>
  );
}

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },
  badge: {
    background: "var(--primary-bg)",
    color: "#D4620D",
    borderRadius: 6,
    padding: "2px 10px",fontSize: 14,
    marginLeft: 8,
  },
  headerActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  btn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,

cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    fontFamily: "Montserrat, sans-serif",
  },
  btnActive: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
  },

  btnExport: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    border: "none",
    background: "#16a34a",
    cursor: "pointer",fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  btnSecondary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,

color: "var(--text-2)",
    cursor: "pointer",
    fontWeight: 700,fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  filters: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",

padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  filterInput: {
    padding: "8px 12px",
    borderRadius: 10,    

fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
  },
  loading: { textAlign: "center", padding: 60, color: "var(--text-2)", fontSize: 14 },
  tableWrap: {
    borderRadius: 14,
    overflow: "auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",fontSize: 13,
    fontWeight: 700,

},
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "11px 16px",
    textAlign: "left",fontSize: 11,
    color: "var(--text-2)",
    fontWeight: 700,
    background: "var(--bg-subtle)",
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  tr: { cursor: "pointer", transition: "background 0.1s" },
  td: {
    padding: "10px 16px",
    borderBottom: "1px solid var(--border)",
    verticalAlign: "middle",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 600,fontSize: 13,
    color: "var(--text)",
  },
  thumb: {
    width: 48,
    height: 48,
    borderRadius: 10,
    objectFit: "cover",
    display: "block",
    background: "var(--bg-subtle)",
  },
  noFacade: { color: "var(--text-3)", fontSize: 16 },
  userName: { fontSize: 13, fontWeight: 700, color: "var(--text)", textTransform: "uppercase" },
  userEmail: { fontSize: 13, color: "var(--text-2)" },
  coords: { fontSize: 13, color: "var(--text-2)" },
  coordsLink: {
    fontSize: 13,
    color: "var(--text-2)",
    textDecoration: "underline",
    cursor: "pointer",
  },
  mapPinLink: {
    color: "var(--error-text)",
    display: "inline-flex",
    alignItems: "center",
    transition: "color 0.15s",
  },
  exportEmpWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 0,

borderRadius: 10,
    overflow: "hidden",

},
  exportEmpSearch: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 12px",
    borderBottom: "1px solid var(--border)",
  },
  exportEmpInput: {
    flex: 1,
    border: "none",
    outline: "none",fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    background: "transparent",
  },
  exportEmpClear: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-3)",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },
  exportEmpList: { maxHeight: 200, overflowY: "auto" },
  exportEmpItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 14px",
    cursor: "pointer",
    transition: "background 0.1s",
    borderBottom: "1px solid var(--border)",
  },
  exportEmpItemActive: { background: "var(--primary-bg)" },
  exportEmpDot: (active) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
    background: active ? "#D4620D" : "#e6e6e2",
    transition: "background 0.15s",
  }),
  exportPage: {
    position: "fixed",
    inset: 0,
    background: "var(--bg-subtle)",
    zIndex: 2000,
    overflowY: "auto",
    padding: "0 0 60px",
  },
  exportPageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 32px",

borderBottom: "1px solid var(--border)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  exportPageTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text)",
    margin: 0,
  },
  exportBackBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "none",
    border: "none",
    cursor: "pointer",fontSize: 14,
    fontWeight: 700,
    color: "var(--text-2)",
    fontFamily: "Montserrat, sans-serif",
  },
  exportDownloadBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    borderRadius: 10,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 700,fontSize: 14,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  exportSummary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "16px 32px 0",
  },
  exportTag: {
    borderRadius: 20,
    padding: "4px 12px",fontSize: 13,
    color: "var(--text-2)",
    fontWeight: 700,
  },
  exportFilterRow: {
    display: "flex",
    gap: 12,
    padding: "24px 32px",
    flexWrap: "wrap",
  },
  exportFilterBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    padding: "14px 20px",
    borderRadius: 14,
    border: "2px solid var(--border)",

cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    minWidth: 140,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  exportFilterBtnActive: {
    borderColor: "#D4620D",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.1)",
  },
  exportFilterBtnLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  exportFilterBtnDesc: { fontSize: 14, fontWeight: 700, color: "var(--text)" },
  exportPanelOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
  },
  exportPanel: {
    borderRadius: 14,
    padding: 24,
    width: "92%",
    maxWidth: 380,
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  },
  exportPanelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text)",
    margin: "0 0 16px",
  },
  exportPanelLabel: {
    display: "block",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 6,
  },
  exportPanelApply: {
    width: "100%",
    padding: "10px 0",
    borderRadius: 12,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,fontSize: 14,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  exportPanelSecondary: {
    flex: 1,
    padding: "10px 0",
    borderRadius: 12,

color: "var(--text-2)",
    fontWeight: 700,fontSize: 13,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  exportColRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 0",
    cursor: "pointer",
  },
  exportRoleToggle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },
  exportRoleToggleBox: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: "2px solid var(--border)",

display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  },
  exportRoleToggleBoxOn: { background: "#D4620D", borderColor: "#D4620D" },
  roleBadge: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    background: "var(--bg-subtle)",
    borderRadius: 6,
    padding: "3px 8px",
    display: "inline-block",
    textTransform: "uppercase",
  },
  notes: {
    fontSize: 13,
    color: "var(--text-2)",

    maxWidth: 120,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginTop: 20,
  },
  pageBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "8px 14px",
    borderRadius: 10,

cursor: "pointer",
    fontWeight: 700,fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  pageInfo: { fontSize: 13, color: "var(--text-2)" },
  mapWrap: {
    height: "62vh",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    background: "var(--bg-card)",
    borderRadius: 16,
    border: "1px solid var(--border)",
    padding: 28,
    width: "94%",
    maxWidth: 860,
    maxHeight: "92vh",
    overflow: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid var(--border)",
  },
  modalTitle: { fontSize: 17, fontWeight: 800, color: "var(--text)", margin: 0 },
  closeBtn: {
    background: "var(--bg-subtle)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    cursor: "pointer",
    color: "var(--text-2)",
    display: "flex",
    alignItems: "center",
    padding: 6,
  },
  modalImages: { display: "flex", gap: 16, marginBottom: 20 },
  imgLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0 0 6px",
  },
  imgBox: {
    background: "var(--bg-subtle)",
    borderRadius: 10,
    height: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  modalImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    display: "block",
  },
  modalBody: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  },
  modalInfo: {},
  modalForm: {},
  fieldLabel: {
    display: "block",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  fieldInput: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,    

fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box",
  },
  btnDelete: {
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    background: "var(--error-bg)",
    color: "var(--error-text)",
    fontWeight: 700, fontSize: 13,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  btnSave: {
    padding: "10px 20px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,fontSize: 13,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  btnAnalyze: {
    padding: "0 14px",
    borderRadius: 10,

    background: "var(--bg-subtle)",
    color: "var(--text-3)",
    fontWeight: 700, fontSize: 13,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  btnCancel: {
    flex: 1,
    padding: "10px 0",
    borderRadius: 10,

color: "var(--text-2)",
    fontWeight: 700,fontSize: 13,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  mapLayout: {
    display: "flex",
    gap: 12,
    height: "62vh",
  },
  sidebar: {
    width: 320,

borderRadius: 14,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  sidebarSearchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderBottom: "1px solid var(--border)",
  },
  sidebarSearchInput: {
    flex: 1,
    border: "none",
    outline: "none",fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    background: "transparent",
  },
  sidebarSearchClear: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-3)",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },
  sidebarCount: {
    padding: "8px 12px",fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  sidebarPagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderTop: "1px solid var(--border)",
    flexShrink: 0,
  },
  sidebarPageBtn: {
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",

borderRadius: 6,

cursor: "pointer",
    color: "var(--text-2)",
  },
  sidebarPageInfo: { fontSize: 13, color: "var(--text-2)", fontWeight: 700 },
  sidebarItem: {
    padding: 12,
    borderBottom: "1px solid var(--border)",
    cursor: "pointer",
  },
  lightboxOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
    cursor: "zoom-out",
  },
  lightboxImg: {
    maxWidth: "92vw",
    maxHeight: "92vh",
    objectFit: "contain",
    borderRadius: 10,
    boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
  },
  lightboxClose: {
    position: "absolute",
    top: 20,
    right: 20,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
  },
  mapOverlay: {
    position: "absolute",
    top: 10,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    background: "rgba(255,255,255,0.92)",
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    color: "#D4620D",
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    pointerEvents: "none",
  },
  loadMoreBanner: {
    position: "absolute",
    bottom: 10,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid var(--border)",
    padding: "6px 8px 6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
    color: "var(--text-2)",
  },
  loadMoreBtn: {
    padding: "5px 13px",
    borderRadius: 14,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  mapWrap: {
    flex: 1,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    position: "relative",
  },
  mapSearchWrap: {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(255,255,255,0.97)",
    borderRadius: 10,
    padding: "5px 10px 5px 12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
  },
  mapSearchInput: {
    border: "none",
    outline: "none",fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    color: "var(--text)",
    width: 200,
    background: "transparent",
  },
  mapSearchClear: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-2)",
    display: "flex",
    alignItems: "center",
    padding: 2,
  },
  mapSearchCount: {
    fontSize: 13,
    fontWeight: 700,
    color: "#D4620D",
    whiteSpace: "nowrap",
    paddingLeft: 4,
    borderLeft: "1px solid var(--border)",
  },
};
